import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { RegulatoryRegisterLink } from '../../components/environmental/RegulatoryRegisterLink';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { AdvisoryQuarantinePanel } from '../../components/environmental/circularity/AdvisoryQuarantinePanel';
import { LifecycleSankey } from '../../components/environmental/circularity/LifecycleSankey';
import { DispositionComparison } from '../../components/environmental/circularity/DispositionComparison';
import { AvoidedGoalBar } from '../../components/environmental/circularity/AvoidedGoalBar';
import {
  useAvoidedGoalBar,
  useCircularityQuery,
  useDispositionComparison,
  useLifecycleSankey,
} from '../../hooks/useEnvironmentalCircularity';

/**
 * SIL-473 — Circularity tab, first live slice (Meta demo centerpiece).
 * Two advisory modules (lifecycle-flows Sankey, disposition comparison) +
 * a goal bar, ALL inside one quarantine panel (`AdvisoryQuarantinePanel`) —
 * this tab never renders a number outside that panel, and never sums,
 * nets, or axis-shares an advisory figure with an official footprint
 * number (SIL-459 register).
 *
 * REAL: Sankey repurpose/disposal legs, disposition comparison (all three
 * columns — every figure derives from the org's real fleet + SIL-420
 * config scalars). SAMPLE: Sankey resale leg, avoided-vs-goal bar (prod has
 * zero marketplace_transaction rows) — both basis-tagged, never hidden.
 *
 * SIL-576 (I2-D4): promoted from a plain Environmental tab to its own
 * CUSTOMER_NAV entry ("Circularity Outcomes", Intelligence › Portfolio
 * Intelligence), following the SIL-542 ESG Reports precedent — URL
 * unchanged, this page now owns its own PageHeader (environmental.tsx
 * steps aside on this route) to keep title and nav label matched. The
 * SIL-459 actuals disclosure footer still renders below via the shared
 * `environmental.tsx` layout (this tab shows actuals, not a plan, so it
 * keeps that footer — unlike carbon-planning, which replaces it).
 */
export const Route = createFileRoute('/environmental/circularity')({
  component: CircularityTab,
});

function CircularityTab() {
  const circularityQ = useCircularityQuery();
  const sankey = useLifecycleSankey();
  const comparison = useDispositionComparison();
  const goalBar = useAvoidedGoalBar();

  return (
    <div className="space-y-4">
      <PageHeader
        title="Circularity Outcomes"
        pinTo="/environmental/circularity"
        subtitle="Lifecycle disposition and avoided-emissions outcomes for this fleet, advisory-only figures quarantined from the official footprint."
      />

      {/* SIL-611: rendered here rather than by the `/environmental` layout —
       * this page owns its own PageHeader (SIL-576), so the layout-level
       * instance would sit above the page title. Here it lands directly under
       * the header, matching Overview / Fleet detail. */}
      <RegulatoryRegisterLink />

      {circularityQ.isLoading && <LoadingShell rows={4} />}
      {circularityQ.isError && (
        <ErrorState
          title="Could not load the Circularity tab"
          error={circularityQ.error}
          onRetry={() => void circularityQ.refetch()}
        />
      )}

      {circularityQ.data && (
        <AdvisoryQuarantinePanel>
          {sankey.data && <LifecycleSankey data={sankey.data} />}

          {comparison.data && (
            <Card padding="md" className="border-sky-200 bg-white/70">
              <DispositionComparison comparison={comparison.data} />
            </Card>
          )}

          <AvoidedGoalBar
            avoidedToDateKgco2e={goalBar.data.avoidedToDateKgco2e}
            goalKgco2e={goalBar.data.goalKgco2e}
            basis={goalBar.basis}
          />
        </AdvisoryQuarantinePanel>
      )}
    </div>
  );
}
